import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import {
  login,
  register,
  UserSession,
  sanitize,
  kebabToCamel,
  kebabToTitle,
  toTitleCase
} from '../utils'
import { appUrl, createUser, masterTeam } from '../appwrite/methods'
import logo from "../assets/tom-logo-icon-black.jpg"
import { ID, Client, Databases, Functions, Teams } from "appwrite";
import { account, Query } from '../appwrite/config'
import { AppProvider } from '@toolpad/core/AppProvider';
import { SignInPage } from '@toolpad/core/SignInPage';
import { useTheme, createTheme } from '@mui/material/styles';
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Link,
  IconButton,
  Button,
  Stack,
  Box,
  Typography,
  Chip,
  TextField,
  Alert,
  AlertTitle,
  CircularProgress,
  Snackbar,
  Divider
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';


const client = new Client()
  .setEndpoint(process.env.REACT_APP_ENDPOINT)
  .setProject(process.env.REACT_APP_PROJECT_ID);

// const account = new Account(client);
const databases = new Databases(client);
const functions = new Functions(client);
const teams = new Teams(client);


const providers = [{ id: 'credentials', name: 'Email and Password' }];

function LogoButton({ redirect }) {
  const navigate = useNavigate();

  return (
    <Button
      variant="text"
      onClick={() => navigate(redirect || '/')}
      sx={{
        position: 'relative',
        display: 'flex',
        mt: '-5%',
        mb: '5%',
        height: '2.5em',
        width: 'fit-content',
        alignItems: 'center'
      }}
    >
      <ArrowBackIosNewOutlinedIcon sx={{ mr: '0.5em', fontSize: '1em' }} />
      {redirect ? 'Back' : 'Home'}&nbsp;
    </Button>
  );
}


export default function Welcome() {
  const navigate = useNavigate();
  const [session, setSession] = useAtom(UserSession);
  const [inviteData, setInviteData] = useState(false);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [alertType, setAlertType] = useState("info");
  const [creating, setCreating] = useState(false);

  const [userTeams, setUserTeams] = useState([]);

  const username = useRef();
  const password = useRef();
  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => event.preventDefault();

  const urlParams = new URLSearchParams(window.location.search);
  const teamID = urlParams.get('team');
  const inviteKey = urlParams.get('key');
  const redirect = urlParams.get('redirect');
  const manualInviteKey = redirect ? new URL(`https://${window.location.host}${redirect}`).searchParams.get('key') : null;

  const BRANDING = {
    logo: (<>
      <LogoButton redirect={redirect}/>
      <Stack direction="row" justifyContent="center" alignItems="center" gap={1.5}>
        <img
          src={logo}
          alt="Takeoff Monkey Widgets Logo"
          style={{ height: 48 }}
        />
        {!manualInviteKey && <>
          <Typography variant="h5" sx={{ cursor: 'default', userSelect: 'none' }}>+</Typography>
          <img
            src={logo}
            alt="Takeoff Monkey Widgets Logo"
            style={{ height: 48 }}
          />
        </>}
      </Stack>
    </>),
    title: 'Takeoff Monkey Widgets',
  };


  useEffect(() => {
    if (!redirect && (!teamID || !inviteKey)) {
      navigate('/');
      return;
    }

    const getSession = async () => {
      try {
        const user = await account.get()
        if (user) {
          setSession(user);
          await teams.list().then((teamsList) => {
            setUserTeams(teamsList.teams);
          })
        }
      } catch (error) {
        setSession(null);
      }

      // Get active invites to determine if this user can join team
      try {
        await functions.createExecution(
          "db-get-document",
          JSON.stringify({
            'database_id': teamID || masterTeam,
            'collection_id': 'invites',
            'document_id': inviteKey || manualInviteKey
          }),
          false,
          '/',
          'GET'
        ).then((data) => {
          const getInviteData = JSON.parse(data.responseBody).response
          if (getInviteData) setInviteData(getInviteData);
          setLoading(false);
        })
      } catch (error) {
        setInviteData(false);
      }
    }
    getSession();
  }, []);

  const handleSignIn = async (/*provider, formData, callbackUrl*/) => {
    const userIDRef = sanitize(username.current?.value) || '';
    const usernameRef = username.current?.value || '';
    const passwordRef = password.current?.value || '';
    var user = null

    if (creating) {
      setAlertType("warning");
      setOpen("User is already in the process of joining...");
      return;
    }

    setAlertType("info");
    setOpen("Joining team... (this make take a minute)");
    setCreating(true);

    // Potentially create new account if needed
    if (!session) {
      await createUser(usernameRef, inviteData?.email, passwordRef, true).catch((error) => {
        console.error(error.message);
        setAlertType("error");
        setOpen(`Make sure to set a username and password, and that an account with the email ${inviteData?.email} does not already exist.`);
        setCreating(false);
        return
      })
      await account.createEmailPasswordSession(inviteData?.email, passwordRef).catch((error) => {
        console.error(error.message);
        setAlertType("error");
        setCreating(false);
        setOpen("An unknown sign-in error occurred. Please try again shortly.");
        return
      })
      user = await account.get()
    }

    // Join the team
    await functions.createExecution(
      "users-create-membership",
      JSON.stringify({
        'key': inviteKey,
        'teamID': teamID,
        'roles': inviteData?.roles || ["editor"],
        'userID': user?.$id || session.$id,
        'name': user?.name || session.name,
      }),
      false,
      '/',
      'GET'
    ).catch((error) => {
      console.error(error.message);
      setAlertType("error");
      setCreating(false);
      setOpen("Error joining team: " + error.message);
      return
    })

    // Create user's widgets collection for each widget in the team
    await functions.createExecution(
      "db-list-collections",
      JSON.stringify({
        "databaseId": teamID,
        "query": [
          Query.startsWith('$id', 'widget_')
        ]
      }),
      false,
      '/',
      'GET'
    ).then((data) => {
      // Prefill user's widgets for each one in the team
      const widgetData = JSON.parse(data.responseBody).response.collections
      widgetData.forEach(widget => {
        console.log(widget)
        let attrs = {}
        widget.attributes.forEach(attribute => {
          attrs[attribute.key] = null
        })
        console.log(widget.$id, widget.name, user?.$id || session.$id, attrs)
        databases.createDocument(
          teamID,
          widget.$id,
          user?.$id || session.$id,
          attrs
        )
      })
    })

    // Delete invite and redirect to team
    await functions.createExecution(
      "db-delete-document",
      JSON.stringify({
        "database_id": teamID,
        "collection_id": "invites",
        "document_id": inviteKey
      }),
      false,
      '/',
      'GET'
    ).then(() => {
      navigate(`/?team=${teamID}`);
      setCreating(false);
    }).catch((error) => {
      console.error(error.message);
      setCreating(false);
      setOpen("Error loading team: " + error.message);
    })
  }


  return (<>
    <AppProvider branding={BRANDING}>
      <Stack direction="row" sx={{ maxWidth: '500px', mx: 'auto' }}>
        <SignInPage
          signIn={handleSignIn}
          providers={providers}
          slotProps={{
            emailField: {
              label: 'Username',
              name: 'username',
              type: 'text',
              autoFocus: false,
              autoComplete: 'username',
              required: true,
              placeholder: 'John Smith',
            },
            passwordField: {
              label: 'Password',
              name: 'password',
              type: 'password',
              required: true,
            },
            form: {
              noValidate: true,
              sx: { '& .MuiInputBase-root': { height: '2.5em', overflow: 'hidden' } }
            },
            submitButton: {
              sx: { mt: 2 }
            }
          }}
          slots={{
            title: () => <>
              <Typography
                variant="h4"
                sx={{ mt: 1, mb: 1, textAlign: 'center' }}
              >
                {manualInviteKey ? "Create account" : `Welcome to\n${kebabToTitle(teamID || '')}`}
              </Typography>
            </>,

            subtitle: () => <>
              {inviteData && inviteData.$databaseId && inviteData.roles?.length > 0 && session && userTeams?.map((teamData) => teamData.$id).includes(teamID) && session?.email === inviteData?.email ?
                <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
                  You are already a member of this team.
                </Alert> :
              inviteData && inviteData.roles?.length > 0 && <Typography variant="h6" sx={{ mt: 1.5, mb: 2, color: 'text.secondary', textAlign: 'center' }}>
                Joining as
                {inviteData?.roles?.map((role) =>
                  <Chip
                    key={role}
                    label={toTitleCase(role)}
                    sx={{ ml: 0.5, mb: 0.25, fontSize: '0.75em' }}
                    variant={true ? "filled" : "outlined"}
                    size="medium"
                  />
                )}
              </Typography>}
              {!session && inviteData && <br/> && <Typography
                variant="body1"
                sx={{ mt: 1, color: 'text.secondary', textAlign: 'center' }}
              >
                <Link href={`/login?redirect=${encodeURIComponent(`/welcome?team=${teamID}&key=${inviteKey}`)}`} sx={{ fontWeight: 'bold' }}>Log in to an account</Link>
                <Divider sx={{ width: '100%', fontSize: '1.1em', my: 1 }}>or</Divider>
                Enter a username and password to create a new account
              </Typography>}
              {session && inviteData && <Alert severity="info" variant="outlined" sx={{ mt: 1, mb: 2.5 }}>
                Signed in as <b>{session?.name}</b> ({session?.email}).&nbsp;
                <br /><Link href={`/login?redirect=${encodeURIComponent(`/welcome?team=${teamID}&key=${inviteKey}`)}&signout=true`}>
                  Switch account
                </Link>
              </Alert>}
            </>,

            ...(loading ? { emailField: () => <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  overflow: 'hidden'
                }}>
                  <CircularProgress />
                </Box> } :
              // Invalid invite if the invite document doesn't exist, or it's not a team creation invite but has no roles attached, or the email of the signed-in user doesn't match the email associated with the invite
              !inviteData || (!manualInviteKey && inviteData.roles?.length === 0) || (session && session?.email !== inviteData.email) ? { emailField: () =>
                <Alert severity="error" variant="outlined">
                  This team invite is expired or invalid.
                </Alert> } :
              session ? { emailField: () => null } :
              { emailField: () => <TextField
                fullWidth
                inputRef={username}
                defaultValue={username.current?.value}
                label="Username"
                variant="outlined"
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { height: '2.5em', overflow: 'hidden' } }}
              />}
            ),

            ...(session || !inviteData ? { passwordField: () => null } :
              { passwordField: () => <FormControl
                variant="outlined"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '2.5em',
                    fontSize: '1em',
                    overflow: 'hidden'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '1em',
                    top: '-6px',
                  },
                  '& .MuiInputLabel-shrink': {
                    top: 0,
                  },
                }}
              >
                <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
                <OutlinedInput
                  id="outlined-adornment-password"
                  inputRef={password}
                  defaultValue={password.current?.value}
                  type={showPassword ? 'text' : 'password'}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'hide password' : 'show password'}
                        onClick={toggleShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        size="small"
                        sx={{ mr: '7px' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Password"
                  size="small"
                />
              </FormControl>}
            ),

            submitButton: () => <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={!inviteData || (!manualInviteKey && inviteData.roles?.length === 0) || (session && session.email !== inviteData.email) || creating}
              sx={{ mt: 2 }}
            >
              {creating ? <CircularProgress size={25} color='inherit' /> :
                inviteData?.$databaseId && userTeams?.map((teamData) => teamData.$id).includes(teamID) ?
                "Login to Team" :
                manualInviteKey ?
                "Create & Continue" :
                "Join Team"
              }
            </Button>,

            rememberMe: () => null
          }}
          sx={{ mx: 'auto' }}
        />
      </Stack>
    </AppProvider>

    <Snackbar
      open={Boolean(open)}
      onClose={() => setOpen(false)}
      autoHideDuration={10000}
      key="snackbar"
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert severity={alertType}>
        {/* <AlertTitle>Error</AlertTitle> */}
        {open}
      </Alert>
    </Snackbar>
  </>);
}